import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import AppIcon from '../../components/common/AppIcon/AppIcon';
import { getAllPendingBOM, markBOMOrdered } from '../../services/bomService';
import { useToast } from '../../context/ToastContext';
import styles from './BillOfMaterials.module.css';

const BillOfMaterials = () => {
  const [groups, setGroups] = useState({}); // { project_name: [items] }
  const [loading, setLoading] = useState(true);
  const [orderingId, setOrderingId] = useState(null);
  const toast = useToast();

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllPendingBOM();
      // Group by project_name
      const grouped = data.reduce((acc, item) => {
        const key = item.project_name || 'Unknown Project';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});
      setGroups(grouped);
    } catch (error) {
      console.error('Error fetching pending BOM:', error);
      toast.error('Failed to load pending BOM items');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleMarkOrdered = async (item) => {
    setOrderingId(item.id);
    try {
      await markBOMOrdered(item.id);
      toast.success(`"${item.item_name}" marked as ordered`);
      // Remove from list
      setGroups(prev => {
        const updated = { ...prev };
        const projectKey = item.project_name;
        updated[projectKey] = updated[projectKey].filter(r => r.id !== item.id);
        if (updated[projectKey].length === 0) delete updated[projectKey];
        return updated;
      });
    } catch (error) {
      toast.error('Failed to update status: ' + error.message);
    } finally {
      setOrderingId(null);
    }
  };

  const totalItems = Object.values(groups).flat().length;

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.bomPage}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Bill of Materials</h1>
          <p className={styles.subtitle}>Pending items awaiting ordering</p>
        </div>
        <div className={styles.countBadge}>
          {totalItems} pending item{totalItems !== 1 ? 's' : ''}
        </div>
      </div>

      {totalItems === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><AppIcon name="approvals" size={34} /></div>
          <h3>All caught up!</h3>
          <p>There are no pending BOM items across any projects.</p>
        </div>
      ) : (
        <div className={styles.groupsContainer}>
          {Object.entries(groups).map(([projectName, items]) => {
            const projectTotal = items.reduce((sum, i) => sum + i.cost * i.quantity, 0);

            return (
              <div key={projectName} className={styles.projectGroup}>
                {/* Project header */}
                <div className={styles.projectHeader}>
                  <div className={styles.projectMeta}>
                    <span className={styles.projectIcon}><AppIcon name="materials" size={20} /></span>
                    <h2 className={styles.projectName}>{projectName}</h2>
                    <span className={styles.projectItemCount}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
                  </div>
                  <span className={styles.projectTotal}>Rs. {projectTotal.toFixed(2)}</span>
                </div>

                {/* Items table */}
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item Name</th>
                        <th>Qty</th>
                        <th>Unit Cost</th>
                        <th>Total</th>
                        <th>Vendor</th>
                        <th>Link</th>
                        <th>Notes</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={item.id} className={styles.itemRow}>
                          <td className={styles.tdIdx}>{idx + 1}</td>
                          <td className={styles.tdName}><strong>{item.item_name}</strong></td>
                          <td className={styles.tdCenter}>{item.quantity}</td>
                          <td className={styles.tdRight}>Rs. {Number(item.cost).toFixed(2)}</td>
                          <td className={styles.tdRight}>
                            <strong>Rs. {(item.cost * item.quantity).toFixed(2)}</strong>
                          </td>
                          <td className={styles.tdMuted}>{item.vendor || '—'}</td>
                          <td>
                            {item.link ? (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.link}
                              >
                                Link: View
                              </a>
                            ) : '—'}
                          </td>
                          <td className={styles.tdMuted}>{item.notes || '—'}</td>
                          <td className={styles.tdAction}>
                            <button
                              className={styles.orderBtn}
                              onClick={() => handleMarkOrdered(item)}
                              disabled={orderingId === item.id}
                              title="Mark as Ordered"
                            >
                              {orderingId === item.id ? 'Pending' : ' Order'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className={styles.subtotalRow}>
                        <td colSpan={4} className={styles.subtotalLabel}>Subtotal</td>
                        <td className={styles.subtotalValue}>Rs. {projectTotal.toFixed(2)}</td>
                        <td colSpan={4} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BillOfMaterials;
